<template>
  <!-- Rendered once by AppRoot.vue and teleported into the shell that hosts it
       (AdminSidebar.vue's column, or the wide Dashboard's right-hand column). -->
  <div class="flex-1 min-h-0 flex flex-col">
    <!-- Tabs: the Dashboard's pinned ones (same chips, no ×), then the closable ones.
         Only once there's more than one, so a lone panel looks as before. -->
    <div v-if="pinnedTabs.length + chips.length > 1" class="flex-shrink-0 flex items-center gap-1 mb-2 overflow-x-auto scrollbar-hide" role="tablist">
      <div
        v-for="p in pinnedTabs" :key="p.key"
        role="tab" :aria-selected="activeKey === p.key"
        @click="activeKey = p.key"
        class="flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1 rounded-md border border-transparent text-xs cursor-pointer select-none transition-colors"
        :class="activeKey === p.key
          ? 'bg-surface-muted dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark font-medium'
          : 'text-fg-muted dark:text-fg-muted-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark'"
      >
        <component :is="p.icon" class="h-3.5 w-3.5 flex-shrink-0" />
        <span>{{ p.label }}</span>
        <span v-if="p.badge" class="min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-4 font-medium text-center">{{ formatBadge(p.badge) }}</span>
      </div>
      <div
        v-for="e in chips" :key="e.key"
        role="tab" :aria-selected="e === current"
        @click="activate(e.key)"
        @auxclick.middle.prevent="close(e.key)"
        class="group flex items-center gap-1.5 min-w-0 max-w-[11rem] flex-shrink-0 pl-2.5 pr-1 py-1 rounded-md border text-xs cursor-pointer select-none transition-colors"
        :class="e === current
          ? 'bg-surface-muted dark:bg-surface-muted-dark border-transparent text-fg-base dark:text-fg-base-dark font-medium'
          : 'border-transparent text-fg-muted dark:text-fg-muted-dark hover:bg-surface-muted dark:hover:bg-surface-muted-dark'"
        :title="tabLabel(e)"
      >
        <component :is="TAB_ICONS[e.type]" class="h-3.5 w-3.5 flex-shrink-0" />
        <span class="truncate">{{ tabLabel(e) }}</span>
        <button
          @click.stop="close(e.key)"
          class="p-0.5 rounded flex-shrink-0 text-fg-faint dark:text-fg-faint-dark hover:text-fg-secondary dark:hover:text-fg-secondary-dark"
          :class="e === current ? '' : 'opacity-0 group-hover:opacity-100'"
          :aria-label="`Close ${tabLabel(e)}`"
        ><XMarkIcon class="h-3 w-3" /></button>
      </div>
    </div>

    <!-- Attendance (Dashboard only) is created only while shown: its auto-scroll
         and load-more observers measure layout, which a hidden tab doesn't have.
         It scrolls itself (`contained`), so its wrapper must not. -->
    <AttendanceTab
      v-if="onDashboard && activeKey === ATTENDANCE && hostProps.teacherName?.()"
      :teacher-name="hostProps.teacherName()" contained class="flex-1 min-h-0 w-full min-w-0"
    />
    <div v-if="onDashboard && activeKey === INVOICES && hostProps.showInvoices?.()" class="flex-1 min-h-0 overflow-y-auto" v-scroll-autohide>
      <StatsTab cards />
    </div>

    <!-- Every tab stays mounted (only the active one shown), so a form in a
         background tab keeps what was typed into it. × / Esc close the active one. -->
    <div
      v-for="e in tabs" :key="e.key" v-show="e === current"
      :ref="el => bodies[e.key] = el"
      class="admin-sidebar-body flex-1 min-h-0 flex flex-col"
      :class="{ 'overflow-y-auto': e.type !== 'contact' }"
      v-scroll-autohide="e.type !== 'contact'"
      @scroll.passive="ev => scrollTops[e.key] = ev.target.scrollTop"
    >
      <SidebarContact
        v-if="e.type === 'contact'"
        :contact-id="e.props.contactId"
        :alternates="e.props.alternates"
        @close="close(e.key)"
        @pick="id => open('contact', { ...e.props, contactId: id })"
        @title="t => e.title = t"
      />
      <SidebarSignups
        v-else-if="e.type === 'signups'"
        v-bind="e.props"
        :pinned="isPinned(e)"
        @close="close(e.key)"
      />
      <component
        v-else
        :is="FORMS[e.type]"
        v-bind="e.props"
        as-panel
        @close="close(e.key)"
        @saved="payload => { e.onSaved?.(payload); close(e.key) }"
      />
    </div>
  </div>
</template>

<script setup>
// The workspace sidebar's tabs and panels — see useAdminSidebar.js for the tab
// semantics. The frame around it (width, resize handle) belongs to the host.
import { computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useAdminSidebar, ATTENDANCE, INVOICES } from '../../composables/useAdminSidebar.js'
import { XMarkIcon, UserIcon, UserPlusIcon, PlusCircleIcon, CreditCardIcon, ClipboardDocumentCheckIcon, BanknotesIcon } from '@heroicons/vue/24/outline'
import { useTeacherBadges, formatBadge } from '../../composables/useTeacherBadges.js'
import SidebarContact from './SidebarContact.vue'
import SidebarSignups from './SidebarSignups.vue'
import AttendanceTab from '../tabs/AttendanceTab.vue'
import StatsTab from '../tabs/StatsTab.vue'
import LessonCreateModal from '../common/LessonCreateModal.vue'
import MembershipCreateModal from '../common/MembershipCreateModal.vue'

const FORMS = {
  'lesson-create':     LessonCreateModal,
  'membership-create': MembershipCreateModal,
}

const TAB_ICONS = {
  'contact':           UserIcon,
  'signups':           UserPlusIcon,
  'lesson-create':     PlusCircleIcon,
  'membership-create': CreditCardIcon,
}
const TAB_LABELS = { 'signups': 'Signups', 'lesson-create': 'New Lesson', 'membership-create': 'New Membership' }
const tabLabel = e => e.title || (e.type === 'signups' && e.props.teacher ? 'Scheduling' : TAB_LABELS[e.type]) || 'Contact'

const { tabs, activeKey, current, available, host, hostProps, open, close, activate, back, isPinned } = useAdminSidebar()

const onDashboard = computed(() => host.value === 'dashboard')
// The Dashboard (App.vue) runs the polling; this only reads the counts. Staff see the
// admin signups list, which has no "waiting on you" group, so only teachers get that badge.
const { badges } = useTeacherBadges()
const pinnedTabs = computed(() => {
  if (!onDashboard.value) return []
  const signups = tabs.value.find(isPinned)
  return [
    { key: ATTENDANCE, label: 'Attendance', icon: ClipboardDocumentCheckIcon, badge: badges.value.attendance },
    ...(hostProps.value.showInvoices?.() ? [{ key: INVOICES, label: 'Invoices', icon: BanknotesIcon, badge: badges.value.invoices }] : []),
    ...(signups ? [{ key: signups.key, label: signups.props.teacher ? 'Scheduling' : 'Signups', icon: UserPlusIcon, badge: signups.props.teacher ? badges.value.signups : 0 }] : []),
  ]
})
const chips = computed(() => tabs.value.filter(e => !isPinned(e)))

// Moving between shells re-parents the panels, and a re-parented scroller
// forgets its scroll position — put each one back.
const bodies = {}
const scrollTops = {}
watch(host, async () => {
  await nextTick()
  for (const [key, top] of Object.entries(scrollTops)) if (bodies[key]) bodies[key].scrollTop = top
}, { flush: 'post' })

// Esc closes the active tab — unless something else claimed it (an open
// combobox marks it handled) or a popup is up (lesson details, the contact
// panel's package/hardship dialogs), whose own Esc/close it's meant for.
// Pinned tabs and Attendance aren't closable, so Esc leaves them alone.
const popupOpen = () => [...document.querySelectorAll('.fixed.inset-0')].some(el => el.getClientRects().length)
function onKeydown(e) {
  if (e.key !== 'Escape' || e.defaultPrevented || !available.value || !current.value || isPinned(current.value) || popupOpen()) return
  back()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onUnmounted(() => document.removeEventListener('keydown', onKeydown))
</script>

<style>
/* The create forms lay out in two columns from the `sm` breakpoint, which is
   about the viewport, not the sidebar. Collapse them to one column while the
   sidebar itself is narrow. */
.admin-sidebar-body { container-type: inline-size; }
@container (max-width: 559px) {
  .admin-sidebar-body .sm\:grid-cols-2 { grid-template-columns: minmax(0, 1fr); }
  .admin-sidebar-body .sm\:col-span-2 { grid-column: auto; }
}
</style>
